import { useCallback, useMemo } from "react";
import type { View } from "@common/types/views/views";
import type { BoardCardFieldColumns } from "@design-system/Board/BoardDisplayPopover";
import {
    getTaskCardFieldConfigs,
    TASK_CARD_COMMENTS_FIELD,
    TASK_CARD_FIXED_FIELDS,
    type TaskColumnConfigs,
} from "./taskCardColumns";
import { withTaskViewBoardDisplay } from "./taskBoardViewDisplay";

const COMMENTS_CONFIG = { name: "Comments" };

const isCommentsColumn = ({ id }: View["columns"][number]) =>
    id === TASK_CARD_COMMENTS_FIELD;

/**
 * The Board gear's card fields: List's columns a card can show, plus the
 * card-only Comments field as one more row. Its eye writes the view's
 * `isCommentsShown` and never reaches List's columns.
 */
export const useTaskCardFieldColumns = ({
    view,
    onViewChange,
    columnConfigs,
    isCommentsShown,
}: {
    view: View;
    onViewChange: (view: View) => void;
    columnConfigs: TaskColumnConfigs;
    isCommentsShown: boolean;
}): BoardCardFieldColumns => {
    const cardFieldConfigs = useMemo(
        () => ({
            ...getTaskCardFieldConfigs(columnConfigs),
            [TASK_CARD_COMMENTS_FIELD]: COMMENTS_CONFIG,
        }),
        [columnConfigs],
    );
    const columns = useMemo(
        () =>
            isCommentsShown
                ? [...view.columns, { id: TASK_CARD_COMMENTS_FIELD }]
                : view.columns,
        [view.columns, isCommentsShown],
    );
    const handleColumnsChange = useCallback(
        (nextColumns: View["columns"]) => {
            const isNextCommentsShown = nextColumns.some(isCommentsColumn);
            onViewChange({
                ...view,
                columns: nextColumns.filter(
                    (column) => !isCommentsColumn(column),
                ),
                ...(isNextCommentsShown !== isCommentsShown && {
                    options: withTaskViewBoardDisplay({
                        options: view.options,
                        change: { isCommentsShown: isNextCommentsShown },
                    }),
                }),
            });
        },
        [view, onViewChange, isCommentsShown],
    );
    return useMemo(
        () => ({
            columns,
            columnConfigs: cardFieldConfigs,
            onColumnsChange: handleColumnsChange,
            fixedColumnIds: TASK_CARD_FIXED_FIELDS,
        }),
        [columns, cardFieldConfigs, handleColumnsChange],
    );
};
